import type * as THREE from 'three'

type ClosableImage = TexImageSource & { close?: () => void }

/** 按 Texture 引用计数管理可关闭图片，避免共享 ImageBitmap 被提前释放。 */
export class ImageBitmapLifetime {
  private readonly references = new WeakMap<object, number>()
  private readonly tracked = new Set<ClosableImage>()
  private readonly closed = new WeakSet<object>()

  track(image: TexImageSource): void {
    if (typeof (image as ClosableImage).close === 'function') {
      this.tracked.add(image as ClosableImage)
    }
  }

  bind(texture: THREE.Texture, image: TexImageSource): void {
    const closable = image as ClosableImage
    if (typeof closable.close !== 'function') return
    this.track(image)
    this.references.set(image, (this.references.get(image) ?? 0) + 1)
    let released = false
    const handleDispose = () => {
      if (released) return
      released = true
      texture.removeEventListener('dispose', handleDispose)
      this.release(closable)
    }
    texture.addEventListener('dispose', handleDispose)
  }

  closeIfUnreferenced(image: TexImageSource): void {
    const closable = image as ClosableImage
    this.track(image)
    if ((this.references.get(image) ?? 0) === 0) this.close(closable)
  }

  dispose(): void {
    for (const image of this.tracked) this.close(image)
    this.tracked.clear()
  }

  private release(image: ClosableImage): void {
    const remaining = (this.references.get(image) ?? 1) - 1
    if (remaining > 0) {
      this.references.set(image, remaining)
      return
    }
    this.references.delete(image)
    this.close(image)
  }

  private close(image: ClosableImage): void {
    if (this.closed.has(image)) return
    this.closed.add(image)
    this.tracked.delete(image)
    image.close?.call(image)
  }
}
